import { createContext, useCallback, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { find, propEq } from 'ramda';
import { useAppSelector } from '../../redux/ReduxHooks';
import { ApplicationActions, RobotActions } from '../../utils/constants';
import type { Graph, PythonStreamData } from './dashboardTypes';

export type LiveGraphStoreApi = {
  getGraphByKey: (key: string) => Graph | undefined;
  getAllGraphs: () => Graph[];
  getGraphRevision: (key: string) => number;
  getKeysRevision: (keys: readonly string[]) => string;
  getLatestStreamValue: (streamKey: string) => number | undefined;
  subscribe: (listener: () => void) => () => void;
  subscribeToKey: (key: string, listener: () => void) => () => void;
  subscribeToKeys: (keys: readonly string[], listener: () => void) => () => void;
  getVersion: () => number;
};

export type LiveGraphStoreInstance = ReturnType<typeof createLiveGraphStore>;

const LiveGraphStoreContext = createContext<LiveGraphStoreApi | null>(null);

const noopSubscribe = () => () => {};

/** Fingerprint for whether a graph's live buffer changed since the last publish. */
export function liveGraphRevision(graph: Graph | undefined): number {
  if (!graph) return 0;
  const last = graph.data.at(-1);
  const time = last?.time;
  if (typeof time !== 'number' || !Number.isFinite(time)) return graph.data.length;
  return graph.data.length * 1e12 + time;
}

function keysSignature(keys: readonly string[]): string {
  return keys.join('\0');
}

function resolveGraphKeysForStreamKey(graphs: readonly Graph[], streamKey: string): string[] {
  return graphs.filter((graph) => graph.series?.some((series) => series.dataKey === streamKey)).map((graph) => graph.key);
}

function getLatestStreamValueFromGraphs(graphs: readonly Graph[], streamKey: string): number | undefined {
  for (const graph of graphs) {
    const hasSeries = graph.series?.some((series) => series.dataKey === streamKey);
    if (!hasSeries) continue;

    for (let i = graph.data.length - 1; i >= 0; i -= 1) {
      const row = graph.data[i] as PythonStreamData | undefined;
      const value = row?.[streamKey];
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
    }
  }
  return undefined;
}

export function LiveGraphStoreProvider({ store, children }: { store: LiveGraphStoreApi; children: ReactNode }) {
  return <LiveGraphStoreContext.Provider value={store}>{children}</LiveGraphStoreContext.Provider>;
}

export function createLiveGraphStore(): LiveGraphStoreApi & {
  publish: (graphs: Graph[]) => void;
  clear: () => void;
} {
  let graphs: Graph[] = [];
  let graphsByKey = new Map<string, Graph>();
  let version = 0;
  const globalListeners = new Set<() => void>();
  const keyListeners = new Map<string, Set<() => void>>();
  const keysListeners = new Map<string, { keys: readonly string[]; listeners: Set<() => void> }>();

  const notifyGlobal = () => {
    for (const listener of globalListeners) {
      listener();
    }
  };

  const notifyKeys = (changedKeys: ReadonlySet<string>) => {
    for (const key of changedKeys) {
      const listeners = keyListeners.get(key);
      if (!listeners) continue;
      for (const listener of listeners) {
        listener();
      }
    }

    for (const entry of keysListeners.values()) {
      if (entry.keys.some((key) => changedKeys.has(key))) {
        for (const listener of entry.listeners) {
          listener();
        }
      }
    }
  };

  return {
    publish: (nextGraphs: Graph[]) => {
      const changedKeys = new Set<string>();
      const nextByKey = new Map<string, Graph>();
      const retained: Graph[] = [];

      for (const next of nextGraphs) {
        const prev = graphsByKey.get(next.key);
        if (prev && liveGraphRevision(prev) === liveGraphRevision(next)) {
          nextByKey.set(next.key, prev);
          retained.push(prev);
        } else {
          nextByKey.set(next.key, next);
          retained.push(next);
          changedKeys.add(next.key);
        }
      }

      for (const key of graphsByKey.keys()) {
        if (!nextByKey.has(key)) changedKeys.add(key);
      }

      graphs = retained;
      graphsByKey = nextByKey;
      if (changedKeys.size > 0) {
        version += 1;
        notifyGlobal();
        notifyKeys(changedKeys);
      }
    },
    clear: () => {
      const changedKeys = new Set(graphsByKey.keys());
      graphs = [];
      graphsByKey = new Map();
      version += 1;
      notifyGlobal();
      if (changedKeys.size > 0) {
        notifyKeys(changedKeys);
      }
    },
    getGraphByKey: (key: string) => graphsByKey.get(key),
    getAllGraphs: () => graphs,
    getGraphRevision: (key: string) => liveGraphRevision(graphsByKey.get(key)),
    getKeysRevision: (keys: readonly string[]) => keys.map((key) => liveGraphRevision(graphsByKey.get(key))).join('|'),
    getLatestStreamValue: (streamKey: string) => getLatestStreamValueFromGraphs(graphs, streamKey),
    subscribe: (listener: () => void) => {
      globalListeners.add(listener);
      return () => globalListeners.delete(listener);
    },
    subscribeToKey: (key: string, listener: () => void) => {
      let listeners = keyListeners.get(key);
      if (!listeners) {
        listeners = new Set();
        keyListeners.set(key, listeners);
      }
      listeners.add(listener);
      return () => listeners?.delete(listener);
    },
    subscribeToKeys: (keys: readonly string[], listener: () => void) => {
      const signature = keysSignature(keys);
      let entry = keysListeners.get(signature);
      if (!entry) {
        entry = { keys: [...keys], listeners: new Set() };
        keysListeners.set(signature, entry);
      }
      entry.listeners.add(listener);
      return () => entry?.listeners.delete(listener);
    },
    getVersion: () => version,
  };
}

function useLiveGraphStoreApi(): LiveGraphStoreApi | null {
  return useContext(LiveGraphStoreContext);
}

/** Whether the robot program is actively running or still streaming live sensor samples. */
export function useDashboardProgramRunning(): boolean {
  const programState = useAppSelector((state) => state.project.programState);
  const liveSensorData = useAppSelector((state) => state.robot?.liveSensorData);
  const { SEND_SUCCEEDED, STARTED, STARTING, STOP, STOPPED } = RobotActions.PROGRAM;

  return (
    programState === SEND_SUCCEEDED ||
    programState === STARTED ||
    programState === STARTING ||
    (liveSensorData != null && programState !== STOP && programState !== STOPPED)
  );
}

function useIsLiveProgramOnDashboard(): boolean {
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const isProgramRunning = useDashboardProgramRunning();
  const { LIVE } = ApplicationActions.DashboardViewMode;
  return dashboardViewMode === LIVE && isProgramRunning;
}

function useLiveGraphSource(): {
  graphs: Graph[];
  getGraphByKey: (key: string) => Graph | undefined;
  version: number;
} {
  const store = useLiveGraphStoreApi();
  const reduxGraphs = useAppSelector((state) => state.application.graphs) as Graph[];
  const useStore = useIsLiveProgramOnDashboard() && store != null;

  const storeVersion = useSyncExternalStore(
    useStore && store ? store.subscribe : noopSubscribe,
    () => (useStore && store ? store.getVersion() : 0),
    () => 0
  );

  const getGraphByKey = useCallback(
    (key: string) => {
      if (useStore && store) {
        return store.getGraphByKey(key);
      }
      return find(propEq(key, 'key'), reduxGraphs) as Graph | undefined;
    },
    [reduxGraphs, store, useStore]
  );

  const graphs = useMemo(() => {
    if (useStore && store) {
      return store.getAllGraphs();
    }
    return reduxGraphs;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- storeVersion bumps on live publish
  }, [reduxGraphs, store, useStore, storeVersion]);

  return { graphs, getGraphByKey, version: useStore ? storeVersion : reduxGraphs.length };
}

/** Single graph by `graph.key` — live store during runs, Redux in historic mode. */
export function useLiveGraph(graphKey: string | undefined): Graph | undefined {
  const store = useLiveGraphStoreApi();
  const useStore = useIsLiveProgramOnDashboard() && store != null;
  const reduxGraph = useAppSelector((state) => {
    if (!graphKey) return undefined;
    return find(propEq(graphKey, 'key'), state.application.graphs as Graph[]) as Graph | undefined;
  });

  const revision = useSyncExternalStore(
    (onStoreChange) => {
      if (!useStore || !store || !graphKey) return () => {};
      return store.subscribeToKey(graphKey, onStoreChange);
    },
    () => {
      if (!graphKey) return 0;
      if (useStore && store) return store.getGraphRevision(graphKey);
      return liveGraphRevision(reduxGraph);
    },
    () => 0
  );

  return useMemo(() => {
    if (!graphKey) return undefined;
    if (useStore && store) return store.getGraphByKey(graphKey);
    return reduxGraph;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revision
  }, [graphKey, reduxGraph, store, useStore, revision]);
}

/** Graphs for the given keys — only re-renders when one of those keys changes. */
export function useLiveGraphKeys(graphKeys: readonly string[]): Graph[] {
  const store = useLiveGraphStoreApi();
  const useStore = useIsLiveProgramOnDashboard() && store != null;
  const reduxGraphs = useAppSelector((state) => state.application.graphs) as Graph[];
  const keysKey = keysSignature(graphKeys);

  const revision = useSyncExternalStore(
    (onStoreChange) => {
      if (!useStore || !store || graphKeys.length === 0) return () => {};
      return store.subscribeToKeys(graphKeys, onStoreChange);
    },
    () => {
      if (graphKeys.length === 0) return '';
      if (useStore && store) return store.getKeysRevision(graphKeys);
      return graphKeys.map((key) => liveGraphRevision(find(propEq(key, 'key'), reduxGraphs) as Graph | undefined)).join('|');
    },
    () => ''
  );

  return useMemo(() => {
    if (graphKeys.length === 0) return [];
    if (useStore && store) {
      return graphKeys.map((key) => store.getGraphByKey(key)).filter((graph): graph is Graph => graph != null);
    }
    return graphKeys.map((key) => find(propEq(key, 'key'), reduxGraphs) as Graph | undefined).filter((graph): graph is Graph => graph != null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revision
  }, [graphKeys, keysKey, reduxGraphs, store, useStore, revision]);
}

/** All graphs for toolbar / multi-graph consumers. */
export function useLiveGraphs(): Graph[] {
  const { graphs, version } = useLiveGraphSource();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- version
  return useMemo(() => graphs, [graphs, version]);
}

/** Latest numeric value for a stream key from live graphs (field/orientation widgets). */
export function useLiveStreamValue(streamKey: string, fallback = 0): number {
  const store = useLiveGraphStoreApi();
  const useStore = useIsLiveProgramOnDashboard() && store != null;
  const reduxGraphs = useAppSelector((state) => state.application.graphs) as Graph[];
  const liveSensorData = useAppSelector((state) => state.robot?.liveSensorData);

  const graphKeys = useMemo(() => {
    const sourceGraphs = useStore && store ? store.getAllGraphs() : reduxGraphs;
    return resolveGraphKeysForStreamKey(sourceGraphs, streamKey);
  }, [reduxGraphs, store, streamKey, useStore]);

  const storeRevision = useSyncExternalStore(
    (onStoreChange) => {
      if (!useStore || !store) return () => {};
      if (graphKeys.length === 0) return store.subscribe(onStoreChange);
      return store.subscribeToKeys(graphKeys, onStoreChange);
    },
    () => {
      if (!useStore || !store) return 0;
      if (graphKeys.length === 0) return store.getVersion();
      return store.getKeysRevision(graphKeys);
    },
    () => 0
  );

  return useMemo(() => {
    if (useStore && store) {
      const fromStore = store.getLatestStreamValue(streamKey);
      if (fromStore != null) return fromStore;
    } else {
      const fromGraphs = getLatestStreamValueFromGraphs(reduxGraphs, streamKey);
      if (fromGraphs != null) return fromGraphs;
    }

    const fromLiveSensor = liveSensorData?.pythonStream?.[streamKey];
    return typeof fromLiveSensor === 'number' && Number.isFinite(fromLiveSensor) ? fromLiveSensor : fallback;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- storeRevision
  }, [fallback, graphKeys, liveSensorData, reduxGraphs, store, storeRevision, streamKey, useStore]);
}

export function useLiveGraphStore(): LiveGraphStoreApi | null {
  return useLiveGraphStoreApi();
}
